import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import {createClient} from './WebsocketService';

// 웹소켓 구조 조건
// 1. 연결 공유하고 있는 모든 컴포넌트가 언마운트되면 구독해제
// 2. 도중에 스프링부트가 실행 중지되거나 네트워크 연결이 끊기면 실시간 감지 후 5초마다 재연결 시도
// 3. 웹소켓 연결함수가 있는 컴포넌트끼리 이동 시 구독해제 후 재연결(HeaderMain.js/SideQuickMenu.js 제외)
// 4. oneComponent.js에서 새로고침해도 3개의 컴포넌트가 중복 및 실패없이 각각 연결에 성공해야하고 console.log에서도 연결시도 후 마지막에 연결해제되면 안 됌
// 5. oneComponent.js에 구독함수가 있는 컴포넌트가 여러개라도 한 번만 연결하여 연결상태값을 공유해야 함

const WebsocketContext = createContext(null);

export const WebsocketProvider = ({ children }) => {
    const stompClientRef = useRef(null);
    const connectingRef = useRef(false); // 중복 연결 방지
    const connectionPromiseRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [subscriptions, setSubscriptions] = useState({});
    const [userKey, setUserKey] = useState(null);
    const [reconnectInterval, setReconnectInterval] = useState(null);

    const unsubscribeAll = () => {
        Object.values(subscriptions).forEach((sub) => sub?.unsubscribe());
        setSubscriptions({});
    };

    const websocketConnect = async (key) => {
        if (stompClientRef.current && isConnected) return stompClientRef.current;

        // 연결 진행 중이면 기존 promise 반환
        if (connectingRef.current && connectionPromiseRef.current) {
            return connectionPromiseRef.current;
        }

        // 연결 시도 시작
        connectingRef.current = true;

        connectionPromiseRef.current = new Promise((resolve, reject) => {
            const client = createClient();

            client.connect({ key }, () => {
                stompClientRef.current = client;
                setUserKey(key);
                setIsConnected(true);

                // 연결 완료 후 cleanup
                clearInterval(reconnectInterval);
                setReconnectInterval(null);
                connectingRef.current = false;
                connectionPromiseRef.current = null;

                // 연결 해제 감지
                client.webSocket.onclose = () => {
                    setIsConnected(false);
                    stompClientRef.current = null;
                    unsubscribeAll();
                    startReconnect(key);
                };

                resolve(client); // 연결 완료된 client 반환
            }, (error) => {
                console.error("WebSocket 연결 실패:", error);
                connectingRef.current = false;
                connectionPromiseRef.current = null;
                reject(error);
            });
        });

        return connectionPromiseRef.current;
    };

    const startReconnect = (key) => {
        if (reconnectInterval) return;

        const interval = setInterval(() => {
            if (!stompClientRef.current || !isConnected) {
                websocketConnect(key).catch(() => {});
            }
        }, 5000);
        setReconnectInterval(interval);
    };

    const subscribe = (destination, callback) => {
        if (!stompClientRef.current) return;
        const sub = stompClientRef.current.subscribe(destination, callback);
        setSubscriptions((prev) => ({ ...prev, [destination]: sub }));
    };

    const unsubscribe = (destination) => {
        if (subscriptions[destination]) {
            subscriptions[destination].unsubscribe();
            setSubscriptions((prev) => {
                const copy = { ...prev };
                delete copy[destination];
                return copy;
            });
        }
    };

    return (
        <WebsocketContext.Provider
            value={{
                stompClient: stompClientRef.current,
                isConnected,
                websocketConnect,
                subscribe,
                unsubscribe,
            }}
        >
            {children}
        </WebsocketContext.Provider>
    );
};

export const useWebSocket = () => useContext(WebsocketContext);
